"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import { TagItem } from "@/types";
import { TagOverlay } from "./TagOverlay";
import { MoveHorizontal } from "lucide-react";

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeAlt?: string;
  afterAlt?: string;
  tags?: TagItem[];
  defaultPosition?: number; // 0 to 100
  aspectRatio?: string;
  imageAspectRatio?: string;
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeAlt = "Room before redesign",
  afterAlt = "Room after redesign with matched products",
  tags = [],
  defaultPosition = 50,
  aspectRatio = "aspect-[16/10]",
  imageAspectRatio,
}: BeforeAfterSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(defaultPosition);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      handleMove(e.touches[0].clientX);
    },
    [isDragging, handleMove]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    },
    [isDragging, handleMove]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  const [showOriginalMobile, setShowOriginalMobile] = useState(false);

  return (
    <div className="w-full flex flex-col">
      {/* MOBILE: Toggle View (hidden on md and up) */}
      <div className="md:hidden relative w-full rounded-2xl shadow-card overflow-hidden bg-paper-deep flex flex-col">
        <div className="relative w-full">
          {/* Base image (Before) */}
          <img
            src={beforeImage}
            alt={beforeAlt}
            className="w-full h-auto max-h-[70vh] object-contain"
            loading="eager"
          />
          
          {/* Overlay image (After) */}
          <img
            src={afterImage}
            alt={afterAlt}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-150 ease-in-out ${showOriginalMobile ? 'opacity-0' : 'opacity-100'}`}
            loading="eager"
          />
          
          {/* Tags (only on After image) */}
          {!showOriginalMobile && tags.length > 0 && <TagOverlay tags={tags} />}
          
          {/* Label Pill */}
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-ink/70 backdrop-blur-sm text-paper text-xs font-medium tracking-wide pointer-events-none z-20">
            {showOriginalMobile ? "BEFORE" : "AFTER"}
          </div>
        </div>
        
        <div className="p-3 bg-paper border-t border-line flex justify-center">
          <button
            onPointerDown={(e) => { e.preventDefault(); setShowOriginalMobile(true); }}
            onPointerUp={() => setShowOriginalMobile(false)}
            onPointerLeave={() => setShowOriginalMobile(false)}
            onPointerCancel={() => setShowOriginalMobile(false)}
            className="px-6 py-2 rounded-full bg-clay text-paper text-sm font-semibold hover:bg-clay-deep transition-colors w-full max-w-xs select-none touch-none"
          >
            {showOriginalMobile ? "Release to see Redesign" : "Hold to see Original"}
          </button>
        </div>
      </div>

      {/* DESKTOP: Slider View (hidden below md) */}
      <div
        ref={containerRef}
        className={`hidden md:block relative w-full ${aspectRatio} overflow-hidden rounded-2xl shadow-card select-none cursor-ew-resize bg-paper-deep`}
        style={imageAspectRatio ? { aspectRatio: imageAspectRatio.replace(":", "/") } : undefined}
        onMouseDown={() => setIsDragging(true)}
      >
        {/* Background: AFTER Image */}
        <img
          src={afterImage}
          alt={afterAlt}
          className="absolute inset-0 w-full h-full object-contain"
          loading="eager"
        />

        {/* Interactive Tag Overlay on the AFTER render */}
        {tags.length > 0 && <TagOverlay tags={tags} />}

        {/* Foreground: BEFORE Image (Clipped) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)` }}
        >
          <img
            src={beforeImage}
            alt={beforeAlt}
            className="absolute inset-0 w-full h-full object-contain"
          />
          {/* Subtle Before pill label */}
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-ink/70 backdrop-blur-sm text-paper text-xs font-medium tracking-wide">
            BEFORE
          </div>
        </div>

        {/* Subtle After pill label */}
        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-clay/85 backdrop-blur-sm text-paper text-xs font-medium tracking-wide pointer-events-none">
          AFTER
        </div>

        {/* Divider Bar & Drag Handle */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-paper shadow-[0_0_10px_rgba(0,0,0,0.3)] z-30"
          style={{ left: `${sliderPosition}%` }}
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-paper shadow-md border border-line flex items-center justify-center text-ink cursor-grab active:cursor-grabbing hover:scale-105 transition-transform">
            <MoveHorizontal className="w-4 h-4 text-ink-soft" />
          </div>
        </div>
      </div>
    </div>
  );
}
